import axios from "axios"

import type { FairUsePlan, FairUseTier } from "@/api/fair-use"
import type { ReasoningEffortOverride } from "@/stores/ai-explorer-store"

import { api } from "@/lib/api-client"

export type { FairUsePlan, FairUseTier }

// =============================================================================
// Fair Use — per-user monthly LLM cost budget (issue #2625, spec section 7).
//
// Admin endpoints live under /api/admin/fair-use/* and are gated by the
// delegable `fair_use` capability (routes/admin/fair_use.py). Every response
// shape below mirrors the backend contract from
// docs/superpowers/specs/2026-08-06-fair-use-design.md section 6.
// =============================================================================

export type FairUseStatus = "ok" | "warn" | "downgrade" | "block"

export type FairUseRequestStatus = "pending" | "approved" | "denied"

export type FairUseUserProfile = {
  name?: string | null
  email?: string | null
  /**
   * Directory department, when the backend resolved one. Optional: the profile
   * lookup is best-effort, so the table falls back to the e-mail address for
   * the secondary line rather than leaving it blank.
   */
  department?: string | null
  company?: string | null
}

/** A pending budget request surfaced inline on an overview row. */
export type FairUsePendingRequest = {
  id: string
  motivation?: string | null
  ts: string
}

/** Where a row's plan came from: an explicit per-user assignment, or the default. */
export type FairUseTierSource = "override" | "default"

/**
 * One top-up on a row, summarised for the row menu's "Revoke" entry.
 *
 * `sek` is the budget the top-up added (blocks × block size at grant time), so
 * the menu can name the amount without re-deriving it from a block size that
 * may have changed since.
 */
export type FairUseRowGrant = {
  id: string
  blocks: number
  sek: number
  ts: string
  reason?: string | null
}

export type FairUseOverviewRow = {
  user: string
  profile?: FairUseUserProfile | null
  cost_sek: number
  cap_sek: number
  percent: number
  status: FairUseStatus
  /** Named plan. Absent on older backends — treat as standard. */
  tier?: FairUsePlan
  /** Whether {@link tier} is an explicit assignment. Absent on older backends. */
  tier_source?: FairUseTierSource
  blocks_granted: number
  pending_request: FairUsePendingRequest | null
  /** Top-ups applied to this user this month, newest first. */
  grants?: FairUseRowGrant[]
  /**
   * Whether this user is a local AI lead.
   *
   * Cosmetic, and ABSENT — not `false` — when the backend could not read the
   * lead roster: "unknown" is not "no". Read a missing key as false.
   */
  is_lead?: boolean
  /**
   * How many distinct users are assigned to this lead. `0` when the row is not
   * a lead; absent under the same conditions as {@link is_lead}.
   */
  lead_assigned_count?: number
  /**
   * The lead(s) THIS user is assigned to — never the users this lead leads.
   * Empty when nobody leads them; absent under the same conditions as
   * {@link is_lead}.
   */
  lead_of?: Array<{ oid: string; name: string }>
}

/**
 * The identity + plan half of a row: which person this is, and which plan they
 * are on. Nothing about a month.
 *
 * Shared by the overview row (which always carries a month's usage, because it
 * IS a rollup) and the people row (which may carry none), so one person cell,
 * one plan Select and one lead badge render both.
 */
export type FairUsePersonIdentity = Pick<
  FairUseOverviewRow,
  "user" | "profile" | "tier" | "tier_source" | "is_lead" | "lead_assigned_count" | "lead_of"
>

/**
 * One row of `GET /api/admin/fair-use/people` — the DIRECTORY, with the month
 * left-joined on.
 *
 * The overview is a view of the month's rollups, so somebody who has spent
 * nothing is simply not in it and could never be given a plan. This row is the
 * inverse: it exists because the person exists. The four usage fields are
 * therefore `null` — not `0` — when {@link has_usage} is false: no rollup was
 * ever written, and "0 SEK of a 250 SEK budget" is a measurement nobody took.
 * The plan fields are always real; a plan is a standing assignment.
 */
export type FairUsePersonRow = Omit<
  FairUseOverviewRow,
  "cost_sek" | "cap_sek" | "percent" | "status"
> & {
  cost_sek: number | null
  cap_sek: number | null
  percent: number | null
  status: FairUseStatus | null
  /** Whether this person has any usage this month at all. */
  has_usage: boolean
}

export type FairUsePeopleResponse = {
  year_month: string
  enabled: boolean
  people: FairUsePersonRow[]
  /** Opaque next-page token; null on the last page and for a search. */
  cursor: string | null
  /**
   * The directory could not be read, so `people` is the month's usage rows
   * only — everyone who HAS spent, and nobody else. The page still renders;
   * the table says why the rest are missing.
   */
  directory_unavailable: boolean
  /** The month walk hit its ceiling: usage figures are a lower bound. */
  truncated: boolean
}

export type FairUseAccessGroup = { id: string; name: string }
export async function getFairUseAccessGroups(): Promise<{ groups: FairUseAccessGroup[] }> {
  return fairUseRequest(() => api.get("/admin/fair-use/people/access-groups"))
}
export async function getFairUseAccessGroupMembers(
  groupId: string,
  cursor?: string
): Promise<{ member_ids: string[]; cursor: string | null }> {
  const params = new URLSearchParams({ group_id: groupId })
  if (cursor) params.set("cursor", cursor)
  return fairUseRequest(() => api.get(`/admin/fair-use/people/access-groups?${params}`))
}

/**
 * Server-side row filter for the overview.
 *
 * The same vocabulary the ops card's chips and KPI tiles use; `blocked` is the
 * wire word for the "Stopped" status and `near` for "≥ the warn threshold".
 */
export type FairUseOverviewFilter = "all" | "pending" | "near" | "blocked" | "custom"

/** Month-wide aggregate totals (server-side aggregates — never a row scan). */
export type FairUseOverviewTotals = {
  users: number
  cost_sek_total: number
}

export type FairUseOverviewResponse = {
  year_month: string
  enabled: boolean
  users: FairUseOverviewRow[]
  /** Opaque next-page token; null on the last page. */
  continuation: string | null
  /**
   * Month-wide totals — computed once and returned on the FIRST page only;
   * every continuation page carries `null` (the backend skips the aggregate
   * query when a continuation token is supplied). Read them from
   * `data.pages[0]` and always null-check.
   */
  totals: FairUseOverviewTotals | null
  /**
   * Top-up size in SEK, when the backend inlines it.
   *
   * Lets the ops card label "Approve +50 SEK" without also holding config
   * read access — a delegated admin may resolve requests without being allowed
   * to read the commercial settings singleton. Optional: absent on older
   * backends, where the card falls back to the config query.
   */
  block_cap_sek?: number
  /**
   * How many users matched the active filter. `null` on the unfiltered (`all`)
   * listing, which is paged rather than matched. Filtered views are unpaged:
   * `users` is `matched[:limit]` and `continuation` is always null.
   */
  matched?: number | null
  /**
   * True when the returned `users` are not the complete matching set — either
   * more users matched than `limit`, or the month walk hit its rollup ceiling.
   * The UI must not present a truncated filter as complete, and must not
   * invent a continuation token (filtered views are unpaged).
   */
  truncated?: boolean
}

/** The month header for the ops card: counts, totals and the billing lock. */
export type FairUseMonthLock = {
  locked: boolean
  locked_at: string | null
}

export type FairUseSummary = {
  year_month: string
  enabled: boolean
  users: number
  cost_sek_total: number
  pending: number
  near_cap: number
  stopped: number
  /**
   * The billing lock — `null` for a customer admin, who may neither see nor
   * change it. A null lock is therefore the signal to hide the whole
   * lock/export affordance, NOT a lock in the "open" state.
   */
  lock: FairUseMonthLock | null
  /** Users on a non-default plan, when the backend counts them. */
  custom_plan?: number
  /** Top-up size in SEK — same role as on the overview response. */
  block_cap_sek?: number
  /**
   * True when the month walk hit its rollup ceiling, so `near_cap` / `stopped`
   * / `custom_plan` are a lower bound rather than the complete month.
   */
  truncated?: boolean
}

/** A budget request document (kind="request" in the llm-usage container). */
export type FairUseRequest = {
  id: string
  user_id: string
  year_month: string
  motivation?: string | null
  status: FairUseRequestStatus
  resolved_by?: string | null
  resolved_at?: string | null
  ts: string
  profile?: FairUseUserProfile | null
}

/** An immutable grant/billing event (kind="grant"). */
export type FairUseGrant = {
  id: string
  user_id: string
  year_month: string
  blocks: number
  block_cap_sek: number
  block_list_price_sek: number
  granted_by: string
  reason?: string | null
  request_id?: string | null
  ts: string
}

export type FairUseOverride = {
  /** Named plan. Standard is the default and is not stored. */
  tier?: FairUseTier
  /** Legacy/custom amount. When set, it wins over the plan's live cap. */
  cap_sek?: number
  reason?: string
  set_by?: string
  ts?: string
}

/**
 * A reasoning-effort stop the platform recognises.
 *
 * Reuses the Explorer ladder deliberately: the backend validates
 * `downgrade_reasoning_effort` against `fair_use/constants.py::
 * VALID_REASONING_EFFORTS`, which is pinned identical to
 * `services.model_capabilities.EFFORT_LADDER` — the very ladder
 * {@link ReasoningEffortOverride} spells out on this side. One type means a new
 * platform effort stop cannot appear in one place and be missing in the other.
 * Type-only import: erased at build time, so no runtime dependency on the store.
 */
export type FairUseReasoningEffort = ReasoningEffortOverride

// =============================================================================
// Plan entitlements (#3087, spec R1) — what a plan may DO, next to what it may
// spend. Platform-only, like the plan amounts themselves.
// =============================================================================

/**
 * The assignable plans, ladder order — the columns of the entitlement matrix.
 *
 * Mirrors `helloai_utilities/fair_use/constants.py::VALID_TIERS`. `custom` is a
 * display-only reading of a legacy per-user cap override and has no
 * entitlement row of its own: the backend resolves it to the Standard row
 * (`FairUseConfig.entitlements_for_tier`), so it is not a column here either.
 */
export const FAIR_USE_ENTITLEMENT_PLANS: readonly FairUseTier[] = ["standard", "pro", "unlimited"]

/**
 * The thirteen entitlement fields, in the canonical order the matrix renders
 * them (`constants.ENTITLEMENT_FIELDS`).
 *
 * Duplicated from the backend rather than read off `entitlement_schema`
 * because this side keeps a TYPED cell per field ({@link FairUseEntitlementRow}):
 * a fourteenth field appearing on the wire has no form state, no editor and no
 * parser here, so it is rendered by a later release rather than guessed at.
 * The schema still supplies the per-field metadata (bounds, effort options) —
 * see {@link FairUseEntitlementSchema}.
 */
export const FAIR_USE_ENTITLEMENT_FIELDS = [
  "max_reasoning_effort",
  "deep",
  "voice",
  "scheduled_flows",
  "flow_max_steps",
  "flow_max_seconds",
  "images_per_month",
  "max_tool_iterations",
  "turn_max_runtime_seconds",
  "deep_token_budget",
  "deep_spend_budget_sek",
  "deep_recursion_limit",
  "capacity_retry_budget",
] as const

export type FairUseEntitlementField = (typeof FAIR_USE_ENTITLEMENT_FIELDS)[number]

/**
 * One plan's complete entitlement row.
 *
 * `null` means "no limit" and is only ever a legal value for the four fields
 * the backend lists in `NULLABLE_ENTITLEMENT_FIELDS`; the rest are real
 * per-turn budgets whose absence would mean an unbounded turn.
 */
export type FairUseEntitlementRow = {
  max_reasoning_effort: FairUseReasoningEffort
  deep: boolean
  voice: boolean
  scheduled_flows: number | null
  flow_max_steps: number | null
  flow_max_seconds: number | null
  images_per_month: number | null
  max_tool_iterations: number
  turn_max_runtime_seconds: number
  deep_token_budget: number
  /**
   * SEK one Deep run may spend (#3089, spec R3) — the platform's only DECIMAL
   * entitlement, because 5.89 SEK is a real budget and rounding it to 5 or 6
   * moves the trip point by up to 9 %.
   */
  deep_spend_budget_sek: number
  deep_recursion_limit: number
  capacity_retry_budget: number
}

/** Every plan's row. Complete on the wire — the backend normalizes on read. */
export type FairUseEntitlements = Record<FairUseTier, FairUseEntitlementRow>

/**
 * SEK per STARTED voice minute, keyed by lower-cased voice deployment id, plus
 * the optional shared `default` key. An absent rate meters 0.00 and logs — it
 * never blocks (spec R5 invariant).
 */
export type FairUseVoicePrices = Record<string, number>

/** The `default` key: one rate covering every voice model without its own. */
export const FAIR_USE_VOICE_PRICE_DEFAULT_KEY = "default"

/** Ceiling for one voice rate (`constants.MAX_VOICE_PRICE_SEK_PER_MINUTE`). */
export const FAIR_USE_MAX_VOICE_PRICE_SEK_PER_MINUTE = 1000

// =============================================================================
// Round-3 price tables (#3089, spec R5/R6) — what an image and a realtime
// voice token cost. Platform-only, like every other invoice input.
// =============================================================================

/**
 * SEK per produced image, keyed `{model: {size: {quality: sek}}}` with every
 * level lower-cased and `default` accepted at each of the three.
 *
 * Three levels because that is how the provider prices images: the same
 * deployment costs several times more at 1536x1024/high than at 1024x1024/low.
 * An unpriced image meters a real 0.00 SEK row and logs once — it is never
 * refused (`constants.DEFAULT_IMAGE_PRICE_SEK`).
 */
export type FairUseImagePrices = Record<string, Record<string, Record<string, number>>>

/** The `default` key, accepted at model, size AND quality level. */
export const FAIR_USE_IMAGE_PRICE_DEFAULT_KEY = "default"

/** Ceiling for one image price (`constants.MAX_IMAGE_PRICE_SEK`). */
export const FAIR_USE_MAX_IMAGE_PRICE_SEK = 1000

/**
 * The five rates one realtime voice model is priced by, in the order the admin
 * surfaces render them (`constants.VOICE_TOKEN_PRICE_FIELDS`).
 *
 * `audio_cached_input` is the discounted rate for the part of the audio prompt
 * the provider served from its own cache. Text has no cached rate — the
 * provider does not report one for realtime — so cached text is billed at
 * `text_input`, which errs high rather than inventing a discount.
 */
export const FAIR_USE_VOICE_TOKEN_PRICE_FIELDS = [
  "audio_input",
  "audio_cached_input",
  "audio_output",
  "text_input",
  "text_output",
] as const

export type FairUseVoiceTokenKind = (typeof FAIR_USE_VOICE_TOKEN_PRICE_FIELDS)[number]

/**
 * SEK per 1M realtime tokens, keyed `{model: {kind: sek}}` plus the optional
 * shared `default` model key.
 *
 * A kind the table omits is priced at 0 by the platform
 * (`FairUseConfig.voice_token_prices_for_model`), and a model with no table at
 * all falls back to {@link FairUseVoicePrices} — per STARTED minute — rather
 * than to nothing.
 */
export type FairUseVoiceTokenPrices = Record<string, Partial<Record<FairUseVoiceTokenKind, number>>>

/** Ceiling for one token rate (`constants.MAX_VOICE_TOKEN_PRICE_SEK_PER_1M`). */
export const FAIR_USE_MAX_VOICE_TOKEN_PRICE_SEK_PER_1M = 10000

/** Structured config (spec section 4) — the settings singleton payload. */
export type FairUseConfig = {
  enabled: boolean
  default_monthly_cap_sek: number
  pro_monthly_cap_sek: number
  unlimited_monthly_cap_sek: number
  warn_percent: number
  request_cta_percent: number
  downgrade_percent: number
  block_percent: number
  downgrade_model: string
  /**
   * Reasoning effort forced on a downgraded turn, alongside the model swap
   * (default `"low"`). Enforcement tuning rather than a commercial term, so it
   * is DELEGABLE — see FAIR_USE_CONFIG_FIELD_IS_PLATFORM_ONLY.
   */
  downgrade_reasoning_effort: FairUseReasoningEffort
  block_cap_sek: number
  block_list_price_sek: number
  overrides: Record<string, FairUseOverride>
  /**
   * Per-plan entitlements (#3087 R1). PLATFORM-ONLY.
   *
   * Optional on this side even though the backend always writes all three
   * plans: a round-1 backend has no such field, and a matrix rendered from
   * shipped defaults would state limits this tenant is not actually on. The
   * panel hides the whole round-2 block when it is absent rather than
   * inventing one — see `fairUseHasEntitlements`.
   */
  entitlements?: FairUseEntitlements
  /** Voice rate per started minute, per voice model. PLATFORM-ONLY. */
  voice_price_sek_per_minute?: FairUseVoicePrices
  /**
   * Round-3 image prices (R5). PLATFORM-ONLY.
   *
   * Optional for the same reason the matrix is: a round-2 backend does not
   * know the field, and sending one it does not know answers 400
   * `invalid_config` and applies NOTHING — the five plan amounts included.
   */
  image_price_sek?: FairUseImagePrices
  /** Round-3 voice token prices (R6), SEK per 1M. PLATFORM-ONLY. */
  voice_token_prices?: FairUseVoiceTokenPrices
  /** SEK one local AI lead may approve per month (R11). DELEGABLE. */
  lead_allowance_sek_per_month?: number
}

/**
 * Per-field metadata for one entitlement column, straight off the platform's
 * own constants.
 *
 * `type` decides the editor (`effort` → Select over `options`, `boolean` →
 * Switch, `integer` / `number` → numeric input), `nullable` whether blank is a legal
 * "no limit", and `minimum`/`maximum` the accepted span. `maximum` may be
 * `null` for a field with no ceiling.
 */
export type FairUseEntitlementSchemaField = {
  name: string
  type: "effort" | "boolean" | "integer" | "number"
  nullable: boolean
  minimum?: number
  maximum?: number | null
  /**
   * True when `minimum` is a floor the value must stay strictly ABOVE — the
   * SEK budget's 0, which the platform refuses (`routes/admin/fair_use.py::
   * _entitlement_schema`). Absent means the floor is inclusive.
   */
  exclusive_minimum?: boolean
  options?: string[]
}

/**
 * The matrix's server-derived schema — a SIBLING of `config`, deliberately not
 * a field inside it: the value hash is computed over `config` alone, so
 * folding platform metadata in would make a backend release read as a
 * concurrent edit (409 `hash_mismatch`) to an admin who changed nothing. Same
 * reasoning as `downgrade_model_options`.
 */
export type FairUseEntitlementSchema = {
  plans: string[]
  fields: FairUseEntitlementSchemaField[]
}

/**
 * One selectable deployment for `downgrade_model`.
 *
 * `id` is the deployment id written to `downgrade_model` verbatim (the PUT
 * payload is unchanged by this list existing); `label` is display-only.
 */
export type FairUseDowngradeModelOption = {
  id: string
  label: string
  /** Informational deployment name behind `id`. */
  deployment?: string
  /**
   * True on the option the STORED `downgrade_model` resolves to.
   *
   * Set by RESOLUTION, not string equality (`model_catalog.py`): a legacy
   * config saying `gpt-5.6-terra` against a `gpt-5.6-terra_eu` deployment is
   * marked selected even though the ids differ. That is exactly why the panel
   * must not decide "this model is not deployed" from an id comparison alone.
   */
  selected?: boolean
}

export type FairUseConfigResponse = {
  config: FairUseConfig
  /** Value hash for optimistic concurrency on PUT (compute_value_hash). */
  hash: string
  /**
   * The chat deployments the platform can actually downgrade to, so the admin
   * picks from what exists instead of typing a deployment id that silently
   * never resolves at enforcement time.
   *
   * Derived server-side from `CONFIG_CHAT_MODELS`, so it is NOT part of the
   * config document, the value hash, or the PUT payload. Optional: an older
   * backend omits it, and the field is then simply absent — see
   * `readFairUseDowngradeModelOptions`, which also tolerates it arriving
   * nested inside `config`.
   */
  downgrade_model_options?: FairUseDowngradeModelOption[]
  /**
   * Plans + per-field metadata for the entitlement matrix. Outside the config
   * document and its value hash, like `downgrade_model_options`; absent on a
   * round-1 backend, where the matrix falls back to its own field list and
   * bounds.
   */
  entitlement_schema?: FairUseEntitlementSchema
}

export type FairUseConfigUpdate = Partial<FairUseConfig>

/**
 * The answer to a config PUT.
 *
 * `config` is the post-write state from the worker that performed the write —
 * the client's ONLY proof that the values it sent were applied, since the route
 * merges `updates` field by field and a dropped field arrives inside a 200. It
 * is typed optional on purpose: it comes off the wire, and a body without it
 * has to surface as "could not confirm" rather than be waved through by the
 * type system into a success message.
 */
export type FairUseConfigUpdateResponse = {
  success: boolean
  config?: FairUseConfig
  hash?: string
}

// =============================================================================
// Errors
// =============================================================================
//
// EVERY admin fair-use failure answers with `{error: <human message>, code:
// <stable id>}` (routes/admin/fair_use.py::_error). The `code` is the contract
// — the message is free text and may be reworded — so the calls below preserve
// both on the thrown error instead of letting react-query surface a bare
// "Request failed with status code 409".

/** The stable `code` values routes/admin/fair_use.py emits. */
export type FairUseErrorCode =
  | "fair_use_not_configured"
  | "invalid_request"
  | "request_not_found"
  | "request_not_pending"
  | "grant_not_found"
  | "grant_already_revoked"
  | "month_locked"
  | "month_in_future"
  | "hash_mismatch"
  | "invalid_config"
  /** 403 on the config PUT: a delegated admin touched a commercial field. */
  | "platform_admin_required"

/**
 * A rejected admin fair-use call.
 *
 * `message` is the server's own explanation (e.g. "Cannot grant into billing
 * month 2026-07: it is locked (since …)"), `code` the machine-readable id
 * callers switch on, `status` the HTTP status. `currentHash` is only set for
 * the config PUT's `hash_mismatch` 409 — the same optimistic-concurrency
 * payload the raw-entity endpoints return (see `_shared.ts`).
 */
export class FairUseApiError extends Error {
  readonly code: string | undefined
  readonly status: number | undefined
  readonly currentHash: string | undefined

  constructor(
    message: string,
    options: { code?: string; status?: number; currentHash?: string } = {}
  ) {
    super(message)
    this.name = "FairUseApiError"
    this.code = options.code
    this.status = options.status
    this.currentHash = options.currentHash
  }
}

/**
 * Convert an axios rejection carrying the fair-use error envelope into a
 * {@link FairUseApiError}. Anything else (network failure, unshaped body) is
 * returned untouched so callers still see the original error.
 */
function toFairUseApiError(error: unknown): unknown {
  if (!axios.isAxiosError(error)) return error
  const data = error.response?.data as
    | { error?: unknown; code?: unknown; current_hash?: unknown }
    | undefined
  const code = typeof data?.code === "string" && data.code ? data.code : undefined
  const serverMessage =
    typeof data?.error === "string" && data.error.trim() ? data.error : undefined
  if (!code && !serverMessage) return error
  return new FairUseApiError(serverMessage ?? error.message, {
    code,
    status: error.response?.status,
    currentHash: typeof data?.current_hash === "string" ? data.current_hash : undefined,
  })
}

/**
 * Run one admin call, normalising its failure shape into
 * {@link FairUseApiError}.
 *
 * Exported so the sibling billing client shares one error contract: the
 * month-lock and export routes answer with the SAME `{error, code}` envelope
 * and the same `month_locked` / `platform_admin_required` codes, so they must
 * not grow a second, divergent mapping.
 */
export async function fairUseRequest<T>(send: () => Promise<unknown>): Promise<T> {
  try {
    const response = (await send()) as { data?: T }
    return (response.data || response) as T
  } catch (error) {
    throw toFairUseApiError(error)
  }
}

export async function getFairUseOverview(
  args: {
    month?: string
    limit?: number
    continuation?: string
    filter?: FairUseOverviewFilter
  } = {}
): Promise<FairUseOverviewResponse> {
  const params = new URLSearchParams()
  if (args.month) params.set("month", args.month)
  if (args.limit != null) params.set("limit", String(args.limit))
  if (args.continuation) params.set("continuation", args.continuation)
  // "all" is the server default — omitted so an older backend that does not
  // know the parameter yet still answers the unfiltered page.
  if (args.filter && args.filter !== "all") params.set("filter", args.filter)
  const query = params.toString()
  return fairUseRequest(() => api.get(`/admin/fair-use/overview${query ? `?${query}` : ""}`))
}

/**
 * The tenant's people, with this month's usage layered on top.
 *
 * `search` is a Graph query (min 2 characters, one page, no cursor), so it is
 * debounced by the caller rather than fired per keystroke.
 */
export async function getFairUsePeople(
  args: { month?: string; search?: string; limit?: number; cursor?: string } = {}
): Promise<FairUsePeopleResponse> {
  const params = new URLSearchParams()
  if (args.month) params.set("month", args.month)
  if (args.search) params.set("search", args.search)
  if (args.limit != null) params.set("limit", String(args.limit))
  if (args.cursor) params.set("cursor", args.cursor)
  const query = params.toString()
  return fairUseRequest(() => api.get(`/admin/fair-use/people${query ? `?${query}` : ""}`))
}

/** Month header: counts, spend and the billing lock (null lock = not platform). */
export async function getFairUseSummary(args: { month?: string } = {}): Promise<FairUseSummary> {
  const query = args.month ? `?month=${encodeURIComponent(args.month)}` : ""
  return fairUseRequest(() => api.get(`/admin/fair-use/summary${query}`))
}

/** The raw request list — the overview inlines pending requests, so this is for drill-down. */
export async function listFairUseRequests(
  args: { status?: FairUseRequestStatus; month?: string } = {}
): Promise<{ requests: FairUseRequest[] }> {
  const params = new URLSearchParams()
  if (args.status) params.set("status", args.status)
  if (args.month) params.set("month", args.month)
  const query = params.toString()
  return fairUseRequest(() => api.get(`/admin/fair-use/requests${query ? `?${query}` : ""}`))
}

/**
 * Revoke a top-up.
 *
 * Platform admins only (403 `platform_admin_required`) and only into an open
 * billing month (409 `month_locked`) — a revoke rewrites what was invoiced, so
 * a closed month may not be touched.
 */
export async function revokeFairUseGrant(args: {
  grantId: string
  reason?: string
  month?: string
}): Promise<{ success: boolean; grant: FairUseGrant }> {
  return fairUseRequest(() =>
    api.post(`/admin/fair-use/grants/${encodeURIComponent(args.grantId)}/revoke`, {
      ...(args.reason ? { reason: args.reason } : {}),
      ...(args.month ? { month: args.month } : {}),
    })
  )
}

export async function approveFairUseRequest(args: {
  requestId: string
  blocks: number
  month?: string
}): Promise<{ success: boolean; grant: FairUseGrant }> {
  return fairUseRequest(() =>
    api.post(`/admin/fair-use/requests/${encodeURIComponent(args.requestId)}/approve`, {
      blocks: args.blocks,
      ...(args.month ? { month: args.month } : {}),
    })
  )
}

export async function denyFairUseRequest(args: {
  requestId: string
  reason?: string
  month?: string
}): Promise<{ success: boolean; request: FairUseRequest }> {
  return fairUseRequest(() =>
    api.post(`/admin/fair-use/requests/${encodeURIComponent(args.requestId)}/deny`, {
      ...(args.reason ? { reason: args.reason } : {}),
      ...(args.month ? { month: args.month } : {}),
    })
  )
}

export async function createFairUseGrant(args: {
  user_id: string
  blocks: number
  reason?: string
  month?: string
}): Promise<{ success: boolean; grant: FairUseGrant }> {
  return fairUseRequest(() =>
    api.post("/admin/fair-use/grants", {
      user_id: args.user_id,
      blocks: args.blocks,
      ...(args.reason ? { reason: args.reason } : {}),
      ...(args.month ? { month: args.month } : {}),
    })
  )
}

export async function getFairUseConfig(): Promise<FairUseConfigResponse> {
  return fairUseRequest(() => api.get("/admin/fair-use/config"))
}

/**
 * Update the structured fair-use config with optimistic concurrency.
 *
 * The server returns 409 with `code: "hash_mismatch"` when the stored value
 * hash has moved since the caller read it (someone else saved in between).
 * That 409 arrives as a {@link FairUseApiError} carrying `code` and
 * `currentHash` — the same information `rethrowHashMismatch` extracts for the
 * raw-entity PUTs, minus the message-prefix convention, because here EVERY
 * failure already carries a code and one mapping handles all of them.
 */
export async function updateFairUseConfig(args: {
  updates: FairUseConfigUpdate
  expectedHash: string
}): Promise<FairUseConfigUpdateResponse> {
  return fairUseRequest(() =>
    api.put("/admin/fair-use/config", {
      updates: args.updates,
      expected_hash: args.expectedHash,
    })
  )
}

export async function setFairUseUserTier(args: {
  userId: string
  tier: FairUseTier
  reason?: string
}): Promise<{ success: boolean; user_id: string; tier: FairUsePlan; cap_sek: number }> {
  return fairUseRequest(() =>
    api.put(`/admin/fair-use/users/${encodeURIComponent(args.userId)}/tier`, {
      tier: args.tier,
      ...(args.reason ? { reason: args.reason } : {}),
    })
  )
}
